// Node built-ins
import { createContext, useContext, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
// Third-party packages
import {
	addDoc,
	collection,
	getDocs,
	query,
	where,
	doc,
	updateDoc,
	deleteDoc,
	type Query,
	type DocumentData
} from 'firebase/firestore';
import {
	getStorage,
	ref,
	getDownloadURL,
	uploadBytesResumable
} from 'firebase/storage';
// Internal imports
import Swal from 'sweetalert2';
import {
	createObjectStore,
	getAllValues,
	putBulkValues,
	deleteDB,
	putValue,
	deleteValue
} from '../data/indexedDBHelper';
import { db, handleEventLog } from '../gcp/config';
import useUserContext from './UserContext';
import { buildFlashcardObject } from './xBuildObjectUtils';
import type {
	Flashcard,
	FlashcardData,
	FlashcardSettings
} from '../data/interface-flashcard';
import type { QuestionBankID } from '../data/interface-question-bank';
import type { IDBPDatabase } from 'idb';

// Add connection lock
let isConnecting = false;
let connectionPromise: Promise<IDBPDatabase | undefined> | null = null;

export type FlashcardContextType = {
	createFlashcard: (card: FlashcardData) => Promise<void>;
	flashcards: Flashcard[] | undefined;
	getFlashcards: (uid?: string) => Promise<Flashcard[]>;
	getAllFlashcards: (questionBankID: QuestionBankID) => Promise<Flashcard[]>;
	flashcardSettings: FlashcardSettings | undefined;
	setFlashcardSettings: (settings: FlashcardSettings | undefined) => void;
	getFlashcardImageURL: (path: string) => Promise<string>;
	saveFlashcardStudyResults: (results: any) => Promise<boolean> | undefined;
	makeCreatedByPrepMed: (card: Flashcard) => void;
	deleteFlashcard: (id: string) => void;
	uploaderProgress: number;
	setUploaderProgress: (progress: number) => void;
	uploadFlashcardImage: (image: File, imageName: string) => Promise<void>;
	updateFlashcard: (card: Flashcard) => Promise<void>;
};
export const FlashcardContext = createContext<FlashcardContextType | null>(
	null
);
export const FlashcardContextProvider = ({
	children
}: {
	children: ReactNode;
}) => {
	const { currentUser } = useUserContext();
	const [flashcards, setFlashcards] = useState<Flashcard[]>();
	const [flashcardSettings, setFlashcardSettings] =
		useState<FlashcardSettings>();
	const [uploaderProgress, setUploaderProgress] = useState(0);

	// CONSTANTS
	const TODAY = Date.now();
	const ONE_DAY_IN_SECONDS = 86400;
	const THIRTY_DAYS = ONE_DAY_IN_SECONDS * 30;
	const VERSION = 1; // Simplified version number
	const FLASHCARDS_DB_NAME = 'idbpmv1';
	const IDB_TABLE = 'flashcards';
	const FLASHCARDS_LAST_PULL_DATE = 'prepmed-last-flashcards-pull-date';

	// #region IDBP HELPERS
	const openDBConnection = async (
		dbName = FLASHCARDS_DB_NAME,
		tableNamesArray = [IDB_TABLE]
	) => {
		try {
			return await createObjectStore(dbName, VERSION, tableNamesArray);
		} catch (error) {
			console.error('Error opening database connection:', error);
			return undefined;
		}
	};
	// #endregion

	const getAllFlashcards = async (
		questionBankID: QuestionBankID
	): Promise<Flashcard[]> => {
		if (currentUser === null) return [];

		try {
			// Clear existing data
			localStorage.removeItem(FLASHCARDS_LAST_PULL_DATE);
			await deleteDB(FLASHCARDS_DB_NAME);

			// Get flashcards from Firestore
			const cards: Flashcard[] = [];
			const recordsRef = collection(db, IDB_TABLE);
			const q: Query<DocumentData> = query(
				recordsRef,
				where('bank', '==', questionBankID)
			);
			const querySnapshot = await getDocs(q);
			querySnapshot?.forEach((record) => {
				const f = buildFlashcardObject(record.id, record.data());
				if (f !== null) {
					cards.push(f);
				}
			});

			// Update state and local storage
			setFlashcards(cards);
			const thisDB = await openDBConnection();
			if (thisDB !== undefined && cards.length > 0) {
				await putBulkValues(thisDB, IDB_TABLE, cards);
			}

			return cards;
		} catch (error) {
			console.error('Error in getAllFlashcards:', error);
			return [];
		}
	};
	/**
	 * Main logic to get all questions
	 */
	const getFlashcards = async (uid?: string): Promise<Flashcard[]> => {
		let data: Flashcard[] = [];
		if (currentUser === null) return data;

		// Wait for any existing connection to complete
		if (isConnecting && connectionPromise !== null) {
			await connectionPromise;
		}

		const localDate: string | null = localStorage.getItem(
			FLASHCARDS_LAST_PULL_DATE
		);
		const lastPullDate: number | null =
			localDate !== null ? Number(JSON.parse(localDate)) : null;

		// DEFINES WHEN TO CLEAR LOCAL STORAGE
		const clearLocalStorage = async () => {
			if (lastPullDate !== null) {
				const isAdmin = currentUser?.isAdmin ?? false;
				const shouldClear = isAdmin
					? (TODAY - lastPullDate) / 1000 > ONE_DAY_IN_SECONDS
					: (TODAY - lastPullDate) / 1000 > THIRTY_DAYS;

				if (shouldClear) {
					// Close any existing connections before deleting
					const thisDB = await openDBConnection();
					if (thisDB !== undefined) {
						thisDB.close();
					}

					// Delete the database and wait for it to complete
					await deleteDB(FLASHCARDS_DB_NAME);
					localStorage.removeItem(FLASHCARDS_LAST_PULL_DATE);
				}
			}
		};
		await clearLocalStorage();

		// GET THE QUESTIONS
		let thisDB: IDBPDatabase | undefined;
		try {
			// Set connection lock
			isConnecting = true;
			connectionPromise = openDBConnection();
			thisDB = await connectionPromise;

			if (thisDB !== undefined) {
				data = await getAllValues(thisDB, IDB_TABLE);
			}
			const hasLocalData = data.length > 0;

			if (!hasLocalData) {
				if (flashcards !== undefined && flashcards.length > 0) {
					// GET FLASHCARDS FROM USESTATE
					data = flashcards;
				} else {
					// GET FLASHCARDS FROM FIRESTORE
					try {
						const prepMedFlashcards = await getPrepMedFlashcards();
						data = await getUserFlashcards(uid ?? '');
						data = [...data, ...prepMedFlashcards];
					} catch (error) {
						console.error('Error getting flashcards from Firestore', error);
					}
				}
			}

			setFlashcards(data);
			if (thisDB !== undefined && data.length > 0) {
				await putBulkValues(thisDB, IDB_TABLE, data);
			} else {
				console.error('No local data found');
			}
		} finally {
			// Always close the database connection and clear locks
			if (thisDB !== undefined) {
				thisDB.close();
			}
			isConnecting = false;
			connectionPromise = null;
		}

		return data;
	};

	const getPrepMedFlashcards = async (): Promise<Flashcard[]> => {
		if (currentUser === null) return [];
		const cards: Flashcard[] = [];
		const recordsRef = collection(db, IDB_TABLE);
		const q: Query<DocumentData> = query(
			recordsRef,
			where('bank', '==', currentUser.examTarget),
			where('createdBy', '==', 'PrepMedRD')
		);

		const querySnapshot = await getDocs(q);
		querySnapshot?.forEach((record) => {
			const f = buildFlashcardObject(record.id, record.data());
			if (f !== null) {
				cards.push(f);
			}
		});

		return cards;
	};

	const getUserFlashcards = async (uid: string): Promise<Flashcard[]> => {
		if (currentUser === null) return [];
		const cards: Flashcard[] = [];
		const recordsRef = collection(db, IDB_TABLE);
		const q: Query<DocumentData> = query(
			recordsRef,
			where('bank', '==', currentUser.examTarget),
			where('createdBy', '==', uid)
		);

		const querySnapshot = await getDocs(q);
		querySnapshot?.forEach((record) => {
			const f = buildFlashcardObject(record.id, record.data());
			if (f !== null) {
				cards.push(f);
			}
		});

		return cards;
	};

	const getFlashcardImageURL = async (path: string) => {
		let downloadURL = '';
		const storage = getStorage();
		const pathReference = ref(storage, `flashcard-images/${path}`);

		// let thisPath = await pathReference.getDownloadURL();
		// return thisPath;
		await getDownloadURL(pathReference)
			.then((url) => {
				// `url` is the download URL for 'images/stars.jpg'
				downloadURL = url;
				// // This can be downloaded directly:
				// const xhr = new XMLHttpRequest();
				// xhr.responseType = 'blob';
				// xhr.onload = (event) => {
				// const blob = xhr.response;
				// };
				// xhr.open('GET', url);
				// xhr.send();

				// // Or inserted into an <img> element
				// const img = document.getElementById('myimg');
				// img?.setAttribute('src', url);
			})
			.catch((error) => {
				// Handle any errors
				console.error(error);
				handleEventLog('error', error);
			});

		return downloadURL;
	};

	const saveFlashcardStudyResults = async (results: any): Promise<boolean> => {
		if (currentUser !== null) {
			const recordsCollection = 'flashcardRecords';
			try {
				await addDoc(collection(db, recordsCollection), {
					...results,
					userID: currentUser.uid,
					date: new Date(),
					examTargetYear: currentUser.examTargetYear,
					examTarget: currentUser.examTarget
				});
				return true;
			} catch (err) {
				console.error('Error saving flashcard record', err);
				handleEventLog('Error savinfg flashcard study record');
				return false;
			}
		} else {
			return false;
		}
	};

	const deleteFlashcard = async (id: string) => {
		const result = await Swal.fire({
			title: 'Borrar Flashcard',
			text: '¿Estás seguro de querer borrar este flashcard?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Si, borrarlo'
		});

		if (result.isConfirmed) {
			try {
				await deleteDoc(doc(db, 'flashcards', id));
				await Swal.fire('Borrado!', 'Flashcard ha sido borrada.', 'success');
			} catch (err) {
				console.log('Delete flashcard failed:', err);
				await Swal.fire('Error!', 'Error al borrar flashcard.', 'error');
			}
		}
	};

	const makeCreatedByPrepMed = (card: Flashcard) => {
		if (currentUser === null || currentUser.person === undefined) return;
		const userRef = doc(db, 'flashcards', card.id);
		updateDoc(userRef, {
			originalCreator: card.createdBy,
			createdBy: 'PrepMedRD',
			lastEditDate: new Date(),
			lastEditBy: `${currentUser.person.firstName} ${currentUser.person.lastName}`
		})
			.then(() => {
				console.log('Document successfully updated!');
			})
			.catch((error) => {
				console.error('Error updating document: ', error);
			});
	};

	const uploadFlashcardImage = async (
		image: File,
		fileName: string
	): Promise<void> => {
		const storage = getStorage();
		const storageRef = ref(storage, `flashcard-images/${fileName}`);
		const task = uploadBytesResumable(storageRef, image);

		return new Promise((resolve, reject) => {
			task.on(
				'state_changed',
				(snapshot) => {
					const progress =
						(snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setUploaderProgress(progress);
				},
				(error) => {
					console.error(error);
					reject(error);
				},
				() => {
					resolve();
				}
			);
		});
	};

	const createFlashcard = async (card: FlashcardData): Promise<void> => {
		const newCard = {
			authorFirstName: currentUser?.person?.firstName ?? '',
			authorLastName: currentUser?.person?.lastName ?? '',
			authorID: currentUser?.uid ?? '',
			originalCreator: `${currentUser?.person?.firstName ?? ''} ${
				currentUser?.person?.lastName ?? ''
			}`,
			bank: currentUser?.examTarget ?? 'enurm',
			back: card.back,
			categories: card.categories,
			front: card.front,
			imageFileName: card.imageFileName,
			createTS: new Date(),
			createdBy: currentUser?.uid ?? ''
		};

		const recordsCollection = 'flashcards';
		await addDoc(collection(db, recordsCollection), newCard)
			.then(async (docRef) => {
				const thisDB = await openDBConnection();
				if (thisDB !== undefined) {
					await putValue(thisDB, IDB_TABLE, {
						...newCard,
						id: docRef.id
					});
					setFlashcards([...(flashcards ?? []), { ...newCard, id: docRef.id }]);
				}
			})
			.catch((error) => {
				console.error('Error adding document: ', error);
			});
	};

	const updateFlashcard = async (card: Flashcard): Promise<void> => {
		const userRef = doc(db, 'flashcards', card.id);
		await updateDoc(userRef, {
			front: card.front,
			back: card.back,
			image: card.imageFileName,
			categories: card.categories,
			lastEditDate: new Date(),
			lastEditBy: currentUser?.uid ?? ''
		});

		const newFlashcards: Flashcard[] | undefined = flashcards?.filter((f) => {
			return f.id !== card.id;
		});
		const data = [...(newFlashcards ?? []), card];
		setFlashcards(data);

		const thisDB = await openDBConnection();
		if (thisDB !== undefined && data.length > 0) {
			await deleteValue(thisDB, FLASHCARDS_DB_NAME, card.id);
			await putValue(thisDB, IDB_TABLE, card);
		}
	};

	return (
		<FlashcardContext.Provider
			value={useMemo(
				() => ({
					createFlashcard,
					flashcards,
					getAllFlashcards,
					getFlashcards,
					flashcardSettings,
					setFlashcardSettings,
					getFlashcardImageURL,
					saveFlashcardStudyResults,
					makeCreatedByPrepMed,
					deleteFlashcard,
					uploaderProgress,
					setUploaderProgress,
					uploadFlashcardImage,
					updateFlashcard
				}),
				[flashcards, flashcardSettings, uploaderProgress, currentUser]
			)}>
			{children}
		</FlashcardContext.Provider>
	);
};
export const useFlashcardContext = () => {
	const context = useContext(FlashcardContext);
	if (context === undefined || context === null) {
		throw new Error(
			'useAdminContext must be used within a AdminContextProvider'
		);
	}
	return context;
};
export default FlashcardContextProvider;
